<template>
  <div class="emp-myinfo-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <button class="back-btn" @click="goBack">
        <v-icon size="24" color="white">mdi-arrow-left</v-icon>
      </button>
      <div class="header-title">내 정보</div>
      <div class="header-spacer"></div>
    </div>

    <div class="page-content">

    <!-- 프로필 카드 -->
    <div class="profile-card">
      <div class="profile-avatar">
        <v-icon size="40" color="white">mdi-account</v-icon>
      </div>
      <div class="profile-info">
        <h2 class="name">{{ pwaUser?.emp_name || '-' }}</h2>
        <p class="job">{{ pwaUser?.emp_job || '-' }}</p>
        <div class="badge" v-if="pwaUser?.emp_isboss === 'Y'">
          <v-icon size="14" color="white">mdi-crown</v-icon>
          <span>대표자</span>
        </div>
      </div>
    </div>
    
    <div class="info-card">
      <!-- 근무 정보 -->
      <div class="info-section">
        <h3>
          <v-icon size="18" color="#059669">mdi-briefcase-outline</v-icon>
          근무 정보
        </h3>
        <div class="info-row">
          <span class="label">소속</span>
          <span class="value">{{ pwaUser?.sisul_name || pwaUser?.m_sisul_name || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">직종</span>
          <span class="value">{{ pwaUser?.emp_job || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">입사일</span>
          <span class="value">{{ formatDate(pwaUser?.emp_in_date) }}</span>
        </div>
        <div class="info-row" v-if="pwaUser?.emp_out_date">
          <span class="label">퇴사일</span>
          <span class="value warning">{{ formatDate(pwaUser?.emp_out_date) }}</span>
        </div>
        <div class="info-row" v-if="workDays">
          <span class="label">근무기간</span>
          <span class="value highlight">{{ workDays }}</span>
        </div>
      </div>
      
      <!-- 기본 정보 -->
      <div class="info-section">
        <h3>
          <v-icon size="18" color="#059669">mdi-card-account-details-outline</v-icon>
          기본 정보
        </h3>
        <div class="info-row">
          <span class="label">이름</span>
          <span class="value">{{ pwaUser?.emp_name || '-' }}</span>
        </div>
        <div class="info-row">
          <span class="label">생년월일</span>
          <span class="value">{{ formatBirthDate(pwaUser?.emp_birth) }}</span>
        </div>
        <div class="info-row">
          <span class="label">사원코드</span>
          <span class="value code">{{ pwaUser?.emp_code || '-' }}</span>
        </div>
      </div>

      <!-- 연락처 정보 -->
      <div class="info-section">
        <h3>
          <v-icon size="18" color="#059669">mdi-phone-outline</v-icon>
          연락처 정보
        </h3>
        <div class="info-row">
          <span class="label">휴대폰</span>
          <span class="value">
            <a v-if="pwaUser?.emp_phone" :href="'tel:' + pwaUser.emp_phone" class="phone-link">
              {{ formatPhone(pwaUser?.emp_phone) }}
            </a>
            <span v-else>-</span>
          </span>
        </div>
        <div class="info-row">
          <span class="label">이메일</span>
          <span class="value">
            <a v-if="pwaUser?.emp_email" :href="'mailto:' + pwaUser.emp_email" class="email-link">
              {{ pwaUser?.emp_email }}
            </a>
            <span v-else>-</span>
          </span>
        </div>
      </div>

      <!-- 계정 정보 -->
      <div class="info-section">
        <h3>
          <v-icon size="18" color="#059669">mdi-account-key-outline</v-icon>
          계정 정보
        </h3>
        <div class="info-row">
          <span class="label">아이디</span>
          <span class="value">
            <span class="code">{{ pwaUser?.emp_id || '-' }}</span>
            <button class="change-link" @click="openIdDialog">
              변경
              <v-icon size="12" color="#059669">mdi-pencil</v-icon>
            </button>
          </span>
        </div>
        <div class="info-row">
          <span class="label">비밀번호</span>
          <span class="value">
            <button class="change-link" @click="openPwDialog">
              변경하기
              <v-icon size="14" color="#059669">mdi-chevron-right</v-icon>
            </button>
          </span>
        </div>
      </div>
    </div>

    <!-- 아이디 변경 다이얼로그 -->
    <v-dialog v-model="showIdDialog" max-width="340" >
      <v-card class="change-dialog">
        <div class="dialog-header">
          <h3>아이디 변경</h3>
          <button class="close-btn" @click="closeIdDialog">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-content">
          <div class="input-group">
            <label>현재 아이디</label>
            <input type="text" :value="pwaUser?.emp_id" disabled class="disabled-input" />
          </div>
          <div class="input-group">
            <label>새 아이디</label>
            <div class="input-with-check">
              <input 
                type="text" 
                v-model="newId" 
                placeholder="4~20자 (영문, 숫자, _)"
                maxlength="20"
                @input="onIdInput"
                :class="{ 
                  'valid': idCheckResult === 'available',
                  'invalid': idCheckResult === 'duplicate' || idCheckResult === 'invalid'
                }"
              />
              <div class="check-status" v-if="newId.length >= 4">
                <v-progress-circular v-if="isCheckingId" indeterminate size="16" width="2" color="#059669" />
                <v-icon v-else-if="idCheckResult === 'available'" size="18" color="#059669">mdi-check-circle</v-icon>
                <v-icon v-else-if="idCheckResult === 'duplicate'" size="18" color="#dc2626">mdi-close-circle</v-icon>
                <v-icon v-else-if="idCheckResult === 'invalid'" size="18" color="#dc2626">mdi-alert-circle</v-icon>
              </div>
            </div>
            <p class="hint" v-if="newId && newId.length < 4">4자 이상 입력해주세요.</p>
            <p class="hint" v-else-if="idCheckResult === 'available'" style="color: #059669;">사용 가능한 아이디입니다.</p>
            <p class="hint error" v-else-if="idCheckResult === 'duplicate'">이미 사용 중인 아이디입니다.</p>
            <p class="hint error" v-else-if="idCheckResult === 'invalid'">영문, 숫자, 언더스코어(_)만 사용 가능합니다.</p>
          </div>
          <div class="input-group">
            <label>비밀번호 확인</label>
            <input 
              type="password" 
              v-model="idCurrentPw" 
              placeholder="현재 비밀번호 입력"
            />
          </div>
        </div>
        <div class="dialog-actions">
          <button class="cancel-btn" @click="closeIdDialog">취소</button>
          <button 
            class="confirm-btn" 
            :disabled="!canChangeId || isChangingId"
            @click="changeId"
          >
            <v-progress-circular v-if="isChangingId" indeterminate size="16" width="2" color="white" />
            <span v-else>변경</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 비밀번호 변경 다이얼로그 -->
    <v-dialog v-model="showPwDialog" max-width="340" >
      <v-card class="change-dialog">
        <div class="dialog-header">
          <h3>비밀번호 변경</h3>
          <button class="close-btn" @click="closePwDialog">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>
        <div class="dialog-content">
          <div class="input-group">
            <label>현재 비밀번호</label>
            <input 
              type="password" 
              v-model="currentPw" 
              placeholder="현재 비밀번호 입력"
            />
          </div>
          <div class="input-group">
            <label>새 비밀번호</label>
            <input 
              type="password" 
              v-model="newPw" 
              placeholder="4자 이상"
            />
            <p class="hint" v-if="newPw && newPw.length < 4">4자 이상 입력해주세요.</p>
          </div>
          <div class="input-group">
            <label>새 비밀번호 확인</label>
            <input 
              type="password" 
              v-model="confirmPw" 
              placeholder="새 비밀번호 다시 입력"
            />
            <p class="hint error" v-if="confirmPw && newPw !== confirmPw">비밀번호가 일치하지 않습니다.</p>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="cancel-btn" @click="closePwDialog">취소</button>
          <button 
            class="confirm-btn" 
            :disabled="!canChangePw || isChangingPw"
            @click="changePw"
          >
            <v-progress-circular v-if="isChangingPw" indeterminate size="16" width="2" color="white" />
            <span v-else>변경</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 주의사항 -->
    <div class="notice-card">
      <v-icon size="18" color="#d97706">mdi-alert-circle-outline</v-icon>
      <p>개인정보 수정이 필요하신 경우 센터 관리자에게 문의해주세요.</p>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import axios from 'axios';

const store = useStore();
const router = useRouter();
const pwaUser = computed(() => store.getters['pwa/pwaUser']);

// 뒤로가기
const goBack = () => {
  router.back();
};

// 아이디 변경
const showIdDialog = ref(false);
const newId = ref('');
const idCurrentPw = ref('');
const isChangingId = ref(false);
const isCheckingId = ref(false);
const idCheckResult = ref<'available' | 'duplicate' | 'invalid' | null>(null);
let idCheckTimeout: ReturnType<typeof setTimeout> | null = null;

const canChangeId = computed(() => {
  return newId.value.length >= 4 && 
         newId.value.length <= 20 && 
         /^[a-zA-Z0-9_]+$/.test(newId.value) &&
         idCurrentPw.value.length > 0 &&
         idCheckResult.value === 'available';
});

// 아이디 입력 시 중복 검사
const onIdInput = () => {
  // 이전 타이머 취소
  if (idCheckTimeout) {
    clearTimeout(idCheckTimeout);
  }
  
  const id = newId.value.trim();
  
  // 길이 체크
  if (id.length < 4) {
    idCheckResult.value = null;
    return;
  }
  
  // 형식 체크
  if (!/^[a-zA-Z0-9_]+$/.test(id)) {
    idCheckResult.value = 'invalid';
    return;
  }
  
  // 현재 아이디와 동일한지 체크
  if (id === pwaUser.value?.emp_id) {
    idCheckResult.value = 'duplicate';
    return;
  }
  
  // 디바운스: 300ms 후에 API 호출
  idCheckTimeout = setTimeout(async () => {
    isCheckingId.value = true;
    try {
      const response = await axios.get(`/api/emp/check-id/${id}`, { withCredentials: true });
      if (response.data.exists) {
        idCheckResult.value = 'duplicate';
      } else {
        idCheckResult.value = 'available';
      }
    } catch (error) {
      console.error('아이디 중복 체크 오류:', error);
      idCheckResult.value = null;
    } finally {
      isCheckingId.value = false;
    }
  }, 300);
};

const openIdDialog = () => {
  newId.value = '';
  idCurrentPw.value = '';
  idCheckResult.value = null;
  showIdDialog.value = true;
};

const closeIdDialog = () => {
  showIdDialog.value = false;
  newId.value = '';
  idCurrentPw.value = '';
  idCheckResult.value = null;
};

const changeId = async () => {
  if (!canChangeId.value || isChangingId.value) return;
  
  isChangingId.value = true;
  try {
    const response = await axios.put(
      `/api/emp/facility/${pwaUser.value.emp_idx}/id`,
      { newId: newId.value, currentPw: idCurrentPw.value },
      { withCredentials: true }
    );
    
    if (response.data.success) {
      store.commit('showSnackbar', { text: '아이디가 변경되었습니다.', color: 'success' });
      // pwaUser 업데이트
      store.commit('pwa/updatePwaUser', { emp_id: newId.value });
      closeIdDialog();
    } else {
      store.commit('showSnackbar', { text: response.data.message || '아이디 변경에 실패했습니다.', color: 'error' });
    }
  } catch (error: any) {
    console.error('아이디 변경 오류:', error);
    const message = error.response?.data?.message || '아이디 변경 중 오류가 발생했습니다.';
    store.commit('showSnackbar', { text: message, color: 'error' });
  } finally {
    isChangingId.value = false;
  }
};

// 비밀번호 변경
const showPwDialog = ref(false);
const currentPw = ref('');
const newPw = ref('');
const confirmPw = ref('');
const isChangingPw = ref(false);

const canChangePw = computed(() => {
  return currentPw.value.length > 0 && 
         newPw.value.length >= 4 && 
         newPw.value === confirmPw.value;
});

const openPwDialog = () => {
  currentPw.value = '';
  newPw.value = '';
  confirmPw.value = '';
  showPwDialog.value = true;
};

const closePwDialog = () => {
  showPwDialog.value = false;
  currentPw.value = '';
  newPw.value = '';
  confirmPw.value = '';
};

const changePw = async () => {
  if (!canChangePw.value || isChangingPw.value) return;
  
  isChangingPw.value = true;
  try {
    const response = await axios.put(
      `/api/emp/facility/${pwaUser.value.emp_idx}/password`,
      { currentPw: currentPw.value, newPw: newPw.value },
      { withCredentials: true }
    );
    
    if (response.data.success) {
      store.commit('showSnackbar', { text: '비밀번호가 변경되었습니다.', color: 'success' });
      closePwDialog();
    } else {
      store.commit('showSnackbar', { text: response.data.message || '비밀번호 변경에 실패했습니다.', color: 'error' });
    }
  } catch (error: any) {
    console.error('비밀번호 변경 오류:', error);
    const message = error.response?.data?.message || '비밀번호 변경 중 오류가 발생했습니다.';
    store.commit('showSnackbar', { text: message, color: 'error' });
  } finally {
    isChangingPw.value = false;
  }
};

// 근무 기간 계산
const workDays = computed(() => {
  if (!pwaUser.value?.emp_in_date) return null;
  
  const startDate = new Date(pwaUser.value.emp_in_date);
  const endDate = pwaUser.value.emp_out_date 
    ? new Date(pwaUser.value.emp_out_date) 
    : new Date();
  
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const years = Math.floor(diffDays / 365);
  const months = Math.floor((diffDays % 365) / 30);
  const days = diffDays % 30;
  
  let result = '';
  if (years > 0) result += `${years}년 `;
  if (months > 0) result += `${months}개월 `;
  if (days > 0 || result === '') result += `${days}일`;
  
  return result.trim();
});

// 날짜 포맷
const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return date;
  }
};

// 생년월일 포맷
const formatBirthDate = (birth: string | null | undefined): string => {
  if (!birth) return '-';
  // YYMMDD 또는 YYYYMMDD 형식 처리
  if (birth.length === 6) {
    const year = parseInt(birth.substring(0, 2));
    const fullYear = year > 30 ? 1900 + year : 2000 + year;
    return `${fullYear}년 ${birth.substring(2, 4)}월 ${birth.substring(4, 6)}일`;
  } else if (birth.length === 8) {
    return `${birth.substring(0, 4)}년 ${birth.substring(4, 6)}월 ${birth.substring(6, 8)}일`;
  } else if (birth.includes('-')) {
    return formatDate(birth);
  }
  return birth;
};

// 전화번호 포맷
const formatPhone = (phone: string | null | undefined): string => {
  if (!phone) return '-';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}-${cleaned.substring(7)}`;
  } else if (cleaned.length === 10) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
  }
  return phone;
};
</script>

<style scoped lang="scss">
.emp-myinfo-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .back-btn {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
  }
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
  
  .header-spacer {
    width: 32px;
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.profile-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 16px;
  margin-bottom: 16px;
}

.profile-avatar {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-info {
  flex: 1;
  
  .name {
    font-size: 20px;
    font-weight: 700;
    color: white;
    margin: 0 0 4px 0;
  }
  
  .job {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    padding: 4px 10px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: white;
  }
}

.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.info-section {
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  h3 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 700;
    color: #059669;
    margin: 0 0 16px 0;
  }
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f9fafb;
  
  &:last-child {
    border-bottom: none;
  }
  
  .label {
    font-size: 14px;
    color: #6b7280;
  }
  
  .value {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    
    &.code {
      font-family: 'Roboto Mono', monospace;
      background: #f3f4f6;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 13px;
    }
    
    &.warning {
      color: #dc2626;
    }
    
    &.highlight {
      color: #059669;
    }
  }
}

.phone-link,
.email-link {
  color: #059669;
  text-decoration: none;
  font-weight: 600;
  
  &:active {
    text-decoration: underline;
  }
}

.change-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #059669;
  font-size: 12px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
  
  &:active {
    background: #ecfdf5;
  }
}

.change-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  
  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    
    h3 {
      margin: 0;
      color: white;
      font-size: 17px;
      font-weight: 700;
    }
    
    .close-btn {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      border-radius: 8px;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
    }
  }
  
  .dialog-content {
    padding: 20px;
    
    .input-group {
      margin-bottom: 16px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 6px;
      }
      
      input {
        width: 100%;
        padding: 12px 14px;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        font-size: 15px;
        outline: none;
        transition: border-color 0.2s;
        
        &:focus {
          border-color: #059669;
        }
        
        &.valid {
          border-color: #059669;
          background: #f0fdf4;
        }
        
        &.invalid {
          border-color: #dc2626;
          background: #fef2f2;
        }
        
        &.disabled-input {
          background: #f3f4f6;
          color: #6b7280;
        }
      }
      
      .input-with-check {
        position: relative;
        display: flex;
        align-items: center;
        
        input {
          flex: 1;
          padding-right: 40px;
        }
        
        .check-status {
          position: absolute;
          right: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
      
      .hint {
        margin: 6px 0 0;
        font-size: 12px;
        color: #9ca3af;
        
        &.error {
          color: #dc2626;
        }
      }
    }
  }
  
  .dialog-actions {
    display: flex;
    gap: 10px;
    padding: 0 20px 20px;
    
    button {
      flex: 1;
      padding: 14px;
      border-radius: 10px;
      font-size: 15px;
      font-weight: 600;
      border: none;
      cursor: pointer;
      transition: opacity 0.2s;
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
    
    .cancel-btn {
      background: #f3f4f6;
      color: #6b7280;
    }
    
    .confirm-btn {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

.notice-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  background: #fffbeb;
  border-radius: 12px;
  border: 1px solid #fde68a;
  
  p {
    margin: 0;
    font-size: 13px;
    color: #92400e;
    line-height: 1.5;
  }
}
</style>
